import React, { useEffect, useRef, useState } from 'react';
import { regionService } from '../../services';
import { Chevron, LISTED_GRAY, MUTED } from './proUi';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
export const ANYWHERE_LABEL = 'ANYWHERE — TYPE A CITY';

/**
 * A city search with autofill — the same geocoder the events wall uses — in the directory's
 * 2px field. Value: { label, lat, lng } | null. Typing searches Mapbox places (falls back to
 * our regions table when there is no token); picking a row sets the city. `ANYWHERE` clears.
 */
export default function CityField({ value, onChange, placeholder = ANYWHERE_LABEL, allowAnywhere = true, id = 'city-field', compact = false, soft = false, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (!q) { setHits([]); return undefined; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        if (MAPBOX_TOKEN) {
          const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?types=place,locality&limit=6&language=en&access_token=${MAPBOX_TOKEN}`;
          const data = await (await fetch(url)).json();
          setHits((data.features || []).map((f) => {
            const ctx = f.context || [];
            const region = ctx.find((c) => (c.id || '').startsWith('region.'))?.short_code?.split('-').pop() || ctx.find((c) => (c.id || '').startsWith('region.'))?.text;
            const country = ctx.find((c) => (c.id || '').startsWith('country.'))?.text;
            const [lng, lat] = f.center || [];
            return { label: [f.text, region || country].filter(Boolean).join(', '), lat, lng };
          }).filter((h) => h.lat != null));
        } else {
          const r = await regionService.search({ q, skip: 0, take: 6 });
          setHits(r.success ? (r.data || []).map((x) => ({ label: x.name, lat: x.centerLat, lng: x.centerLng })) : []);
        }
      } catch { setHits([]); }
      setSearching(false);
    }, 200);
    return () => clearTimeout(t);
  }, [query, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const pick = (h) => { onChange(h); setQuery(''); setHits([]); setOpen(false); };
  const openIt = () => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); };
  const row = 'block w-full px-3 py-2.5 text-left font-mono font-bold uppercase';
  const rowStyle = { fontSize: 11, letterSpacing: '0.05em' };

  // The Pro editor's BASED IN: one pill input you type into; hits drop below in a 2px-inset card.
  if (soft) {
    const shown = open ? query : (value ? value.label : '');
    return (
      <div ref={wrapRef} className="relative">
        <input ref={inputRef} id={id} value={shown} disabled={disabled} data-testid="city-field" aria-label="Type a city" placeholder={placeholder} autoComplete="off"
          onFocus={() => { setQuery(''); setOpen(true); }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && hits[0]) { e.preventDefault(); pick(hits[0]); } if (e.key === 'Escape') setOpen(false); }}
          className="pr-pill-input font-mono w-full"
          style={{ height: 50, padding: '0 20px', border: 'none', borderRadius: 999, background: '#F5F5F5', fontSize: 14, letterSpacing: '.06em', color: '#000', boxSizing: 'border-box', textTransform: 'uppercase' }} />
        {open && (query.trim() || (allowAnywhere && value)) && (
          <div className="absolute left-0 right-0 z-20 font-mono" style={{ top: '100%', marginTop: 6, padding: 6, borderRadius: 20, background: '#fff', boxShadow: 'inset 0 0 0 2px #000' }} role="listbox">
            {allowAnywhere && !query.trim() && (
              <button type="button" role="option" aria-selected={!value} onMouseDown={(e) => { e.preventDefault(); pick(null); }} className="block w-full text-left font-mono hover:bg-gray-100" style={{ padding: '11px 14px', border: 'none', borderRadius: 14, background: 'transparent', fontSize: 12, letterSpacing: '.08em', cursor: 'pointer' }}>{ANYWHERE_LABEL}</button>
            )}
            {query.trim() && searching && hits.length === 0 && <div style={{ padding: '11px 14px', fontSize: 10, letterSpacing: '.1em', color: MUTED }}>SEARCHING…</div>}
            {query.trim() && !searching && hits.length === 0 && <div style={{ padding: '11px 14px', fontSize: 10, letterSpacing: '.1em', color: MUTED }}>NO CITY MATCHES.</div>}
            {hits.map((h, i) => (
              <button key={`${h.label}-${i}`} type="button" role="option" aria-selected={value?.label === h.label} onMouseDown={(e) => { e.preventDefault(); pick(h); }}
                className="block w-full text-left font-mono hover:bg-gray-100" style={{ padding: '11px 14px', border: 'none', borderRadius: 14, background: 'transparent', fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer' }}>{h.label}</button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <button type="button" id={id} onClick={() => (open ? setOpen(false) : openIt())} aria-haspopup="listbox" aria-expanded={open} data-testid="city-field"
        className="flex w-full items-center justify-between gap-3 border-2 border-black bg-white px-3 text-left font-mono uppercase" style={{ fontSize: 12, letterSpacing: '0.05em', height: compact ? 36 : 40 }}>
        <span className="truncate" style={value ? undefined : { color: LISTED_GRAY }}>{value ? value.label : placeholder}</span>
        <Chevron />
      </button>
      {open && (
        <div className="absolute left-0 right-0 z-20 mt-1 border-2 border-black bg-white" style={{ boxShadow: '4px 4px 0 0 #000' }} role="listbox">
          <div className="border-b-2 border-black p-2">
            <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="TYPE A CITY" aria-label="Type a city"
              onKeyDown={(e) => { if (e.key === 'Enter' && hits[0]) pick(hits[0]); if (e.key === 'Escape') setOpen(false); }}
              className="w-full border-2 border-black px-2 py-1.5 font-mono uppercase outline-none" style={rowStyle} />
          </div>
          {allowAnywhere && !query.trim() && (
            <button type="button" role="option" aria-selected={!value} onClick={() => pick(null)} className={`${row} ${!value ? 'bg-black text-white' : 'hover:bg-gray-50'}`} style={rowStyle}>{ANYWHERE_LABEL}</button>
          )}
          {query.trim() && searching && hits.length === 0 && <div className="px-3 py-2.5 font-mono uppercase" style={{ fontSize: 10, color: MUTED }}>SEARCHING…</div>}
          {query.trim() && !searching && hits.length === 0 && <div className="px-3 py-2.5 font-mono uppercase" style={{ fontSize: 10, color: MUTED }}>NO CITY MATCHES.</div>}
          {hits.map((h, i) => (
            <button key={`${h.label}-${i}`} type="button" role="option" aria-selected={value?.label === h.label} onClick={() => pick(h)}
              className={`${row} border-t-2 border-black ${value?.label === h.label ? 'bg-black text-white' : 'hover:bg-gray-50'}`} style={rowStyle}>{h.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

/** The viewer's stored location from the events wall — { lat, lng, label } — or null. */
export function storedViewerLocation() {
  try {
    const raw = localStorage.getItem('pr.userLocation');
    const v = raw ? JSON.parse(raw) : null;
    if (v && typeof v.lat === 'number' && typeof v.lng === 'number') return { lat: v.lat, lng: v.lng, label: v.label && v.label !== 'Your location' ? v.label : 'NEAR YOU' };
  } catch { /* no stored location */ }
  return null;
}
